import React, { useEffect, useState } from "react";
import { useTheme, useMediaQuery, Button, IconButton, Menu, MenuItem ,Box} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import KitchenIcon from "@mui/icons-material/Kitchen";

import { useTables } from "../../../../../../../../context/OrderCreator/TablesContext";
import OrderInfoCard from "./OrderInfoCard";
import OrderItemsList from "./OrderItemsList";
import EditOrderForm from "../TabletOrderActions/EditOrderForm";
import EditItemModal from "../TabletItemsActions/EditItemModal";
import MoveItemModal from "../TabletItemsActions/MoveItemModal";
import { useOrder } from "../../../../../../../../context/OrderCreator/TablesContext/OrderContext";
import { usePermissions } from "../../../../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import { useFeatures } from "../../../../../../../../context/PermissionsAndFeatures/FeaturesContext";
import { useSettings } from "../../../../../../../../context/SettingsContext"; // NEW: read settings
import { changeOrderItemStatus as changeOrderItemStatusApi } from "../../../../../../../../services/Settings/Orderprocessing";
import { handleBrowserPrint } from "../../../../../../../../components/PagesComponents/PosSection/PrintTemplates/Taghez/printUtilsTaghez";
import { makeItemsInPreparation, printItems } from "../../../../../../../../services/Settings/printers";

const OrderDetailsFetcher = ({
  orderId,
  onReload,
  onClose,
  onOrderDeleted,
}) => {
  const theme = useTheme();
  // ✅ كشف دقيق لجميع مقاسات التابلت والـ iPad
  const isXSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 767px)");
  const isSmallTablet = useMediaQuery("(min-width: 768px) and (max-width: 834px)");
  const isMediumTablet = useMediaQuery("(min-width: 835px) and (max-width: 1024px)");
  const isLargeTablet = useMediaQuery("(min-width: 1025px) and (max-width: 1366px)");
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  const {
    orderDetails,
    loadOrder,
    deleteItem,
    updateItem,
    moveItem,
    removeOffer,
    deleteOrder,
    loading,
    error,
  } = useOrder();

  const { settings = {}, loading: settingsLoading } = useSettings() || {};
  const preparationCasherEnabled = settings?.preparation_casher_button === 0 || settings?.preparation_casher_button === "0";

  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedMoveItem, setSelectedMoveItem] = useState(null);
  const [kitchenMenuAnchor, setKitchenMenuAnchor] = useState(null);
  const [sentItems, setSentItems] = useState([]);
  const { tables, loading: tablesLoading, error: tablesError, refresh: refreshTables } = useTables();
  const { hasPermission } = usePermissions();
  const { hasFeature, featuresLoading } = useFeatures();
  const printingEnabled = !featuresLoading && hasFeature("printing_control");

  // compute pending items early (used by keyboard shortcut and print handler)
  const pendingItems =
    orderDetails?.items?.filter((item) => item.status === "pending") || [];

  useEffect(() => {
    if (orderId) loadOrder(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, loadOrder]);

  useEffect(() => {
    if (onReload) onReload(() => loadOrder(orderId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReload, orderId, loadOrder]);

  // add F9 handler for print preparation (uses pendingItems safely)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F9" || e.keyCode === 120) {
        e.preventDefault();
        if (
          hasPermission("orders_tables_print_preparation_table") &&
          printingEnabled &&
          pendingItems.length > 0
        ) {
          handlePrintPreparation();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // include dependencies that affect the decision
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, printingEnabled, pendingItems.length, settings?.printing_method]);

  const handleDeleteItem = async (itemId) => {
    if (!orderDetails) return alert("❌ لا توجد تفاصيل للطلب حالياً.");
    const item = orderDetails.items?.find((i) => String(i.id) === String(itemId));
    if (!item) return alert("❌ لم يتم العثور على الصنف للحذف.");
    if (!window.confirm("❗ هل تريد حذف هذا الصنف من الطلب؟")) return;
    try {
      if (item.is_offer === 1) {
        await removeOffer({ order_id: orderDetails.id, offer_id: item.id });
        setSuccessMsg("✅ تم حذف العرض من الطلب بنجاح");
      } else {
        await deleteItem(item.id);
        setSuccessMsg("✅ تم حذف الصنف بنجاح");
      }
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في حذف الصنف / العرض");
    }
  };

  const handleUpdateItem = async (updatedData) => {
    try {
      await updateItem(updatedData);
      setSuccessMsg("✅ تم تعديل بيانات الصنف بنجاح");
      setItemEditOpen(false);
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في تعديل الصنف");
    }
  };

  const handleMoveItem = async (data) => {
    try {
      await moveItem(orderId, data);
      setSuccessMsg("✅ تم نقل الصنف بنجاح");
      setMoveModalOpen(false);
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
      if (typeof window.updateTableData === "function") {
        window.updateTableData(parseInt(data.target_table));
      }
    } catch (err) {
      alert(err.message || "❌ فشل في نقل الصنف");
    }
  };

  const handlePrepareItem = async (item) => {
    if (!item) return;
    if (!window.confirm(`هل تريد وضع الصنف "${item.product_name || item.name || item.id}" في حالة التجهيز؟`)) return;
    try {
      await updateItem({ id: item.id, status: "preparing" });
      setSuccessMsg("✅ تم وضع الصنف قيد التجهيز");
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في وضع الصنف قيد التجهيز");
    }
  };

  const changeOrderItemStatus = async (itemId) => {
    if (!itemId) return;
    if (!orderDetails) return alert("❌ لا توجد تفاصيل للطلب حالياً.");
    const item = orderDetails.items?.find((i) => String(i.id) === String(itemId));
    if (!item) return alert("❌ لم يتم العثور على الصنف.");
    if (!window.confirm(`هل تريد وضع الصنف "${item.product_name || item.name || item.id}" قيد التجهيز؟`)) return;

    try {
      if (typeof changeOrderItemStatusApi === "function") {
        await changeOrderItemStatusApi(itemId);
      } else {
        await updateItem({ id: itemId, status: "preparing" });
      }
      setSuccessMsg("✅ تم وضع الصنف قيد التجهيز");
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في تغيير حالة الصنف");
    }
  };

const handlePrintPreparation = async () => {
  if (!orderDetails) return alert("❌ لا توجد بيانات للطلب.");
  if (pendingItems.length === 0) return alert("❌ لا توجد أصناف قيد التجهيز للطباعة.");

  const printingMethod = (settings?.printing_method || "").toLowerCase();

  try {
    const grouped = groupItemsByKitchen(pendingItems);
    const kitchenGroups = Object.values(grouped);

    const afterMark = async () => {
      try {
        await loadOrder(orderId, true);
      } catch (e) {
        console.warn("⚠️ فشل تحديث تفاصيل الأوردر بعد تغيير الحالة:", e);
      }
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails?.table_id },
        })
      );
    };

    // ✅ هنجمّع كل ids اللي اتطبعت (اختياري)
    const allPrintedIds = [];

    // ✅ اطبع مجموعة مجموعة (كل مطبخ لوحده)
    for (const group of kitchenGroups) {
      const itemsForKitchen = group.items;
      const idsForKitchen = itemsForKitchen.map((it) => it.id).filter(Boolean);

      // لو مفيش ids skip
      if (idsForKitchen.length === 0) continue;

      if (printingMethod === "manuel" || printingMethod === "manual") {
        // ✅ طباعة باك-إند لكل مطبخ لوحده
        await printItems(idsForKitchen);
      } else {
        // ✅ طباعة المتصفح لكل مطبخ لوحده
        // (اختياري) ابعت kitchen_name للتمبليت لو هتضيفه فوق
        await handleBrowserPrint({
          order: {
            ...orderDetails,
            _kitchen_name: group.kitchen_name,   // optional helper field
            _kitchen_id: group.kitchen_id,       // optional helper field
          },
          items: itemsForKitchen,
        });
      }

      allPrintedIds.push(...idsForKitchen);
    }

    // ✅ بعد ما نخلص كل المطابخ: نغيّر الحالة لكل اللي اتطبع
    const uniqueIds = Array.from(new Set(allPrintedIds));
    if (uniqueIds.length > 0) {
      await makeItemsInPreparation(uniqueIds);
      await afterMark();
    }

    setSuccessMsg("✅ تم طباعة الأصناف منفصلة لكل مطبخ وتم وضعها قيد التجهيز");
  } catch (err) {
    console.error("❌ خطأ في معالج طباعة التجهيز:", err);
    alert(err?.message || "❌ حدث خطأ أثناء طباعة التجهيز");
  }
};

    const groupItemsByKitchen = (items = []) => {
      return items.reduce((acc, item) => {
        const key = item?.kitchen_id ?? "no_kitchen";
        if (!acc[key]) acc[key] = { kitchen_id: key, kitchen_name: item?.kitchen_name || "بدون مطبخ", items: [] };
        acc[key].items.push(item);
        return acc;
      }, {});
    };


  const handleDeleteOrder = async () => {
    if (!orderDetails) return alert("❌ لا توجد تفاصيل للطلب حالياً.");
    if (!window.confirm("❗ هل أنت متأكد من حذف الطلب بالكامل؟")) return;
    try {
      await deleteOrder(orderDetails.id);
      setSuccessMsg("✅ تم حذف الطلب بنجاح");
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: orderDetails.table_id },
        })
      );
      if (onOrderDeleted) onOrderDeleted();
      if (onClose) onClose();
    } catch (err) {
      alert(err.message || "❌ فشل في حذف الطلب");
    }
  };

  // ✅ حساب الأحجام ديناميكياً
  const getButtonSize = () => {
    if (isXSmallTablet) return "small";
    if (isSmallTablet) return "medium";
    return "medium";
  };
  
  const getSpacing = () => {
    if (isXSmallTablet) return isPortrait ? 1.5 : 2;
    if (isSmallTablet) return isPortrait ? 2 : 2.5;
    if (isMediumTablet) return isPortrait ? 2.5 : 3;
    return 3;
  };
  
  const getPadding = () => {
    if (isXSmallTablet) return isPortrait ? 1.5 : 2;
    if (isSmallTablet) return isPortrait ? 2 : 2.5;
    if (isMediumTablet) return isPortrait ? 2.5 : 3;
    return 3;
  };
  
  const getFontSize = () => {
    if (isXSmallTablet) return { button: 13, title: 16 };
    if (isSmallTablet) return { button: 14, title: 17 };
    if (isMediumTablet) return { button: 15, title: 18 };
    return { button: 15, title: 18 };
  };
  
  const shouldStackVertically = 
    (isXSmallTablet && isPortrait) ||
    (isSmallTablet && isPortrait);
    
  const buttonSize = getButtonSize();
  const spacing = getSpacing();
  const padding = getPadding();
  const fontSize = getFontSize();

  if (loading) return <p className="text-gray-500">جاري تحميل التفاصيل...</p>;
  if (error) return <p className="text-red-500">❌ {error}</p>;
  if (!orderDetails) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: shouldStackVertically ? "column" : "row",
          gap: spacing,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Order Info (left on wide screens, top on narrow) */}
        <Box
          sx={{
            flex: shouldStackVertically ? "0 0 auto" : "0 0 45%",
            minWidth: 0,
            width: "100%",
            mb: shouldStackVertically ? spacing : 0,
          }}
        >
          <OrderInfoCard
            details={orderDetails}
            onEdit={(data) => {
              setEditData({
                ...data,
                order_id: orderDetails.id,
                customer_id: orderDetails.customer?.id || "",
                customer_phone1: orderDetails.customer?.phone1 || "",
                customer_phone2: orderDetails.customer?.phone2 || "",
                customer_phone3: orderDetails.customer?.phone3 || "",
                customer_address1: orderDetails.customer?.address1 || "",
                customer_address2: orderDetails.customer?.address2 || "",
              });
              setEditOpen(true);
            }}
            tables={tables}
            onDelete={handleDeleteOrder}
          />

          {hasPermission("orders_tables_print_preparation_table") && (
            <Box
              sx={{
                display: "flex",
                justifyContent: shouldStackVertically ? "center" : "flex-end",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5, md: 2 },
                mt: spacing,
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={(e) => setKitchenMenuAnchor(e.currentTarget)}
                sx={{
                  background: "linear-gradient(135deg, #fbc2eb 0%, #ff9a9e 100%)",
                  color: "#fff",
                  width: { xs: 40, sm: 44, md: 48 },
                  height: { xs: 40, sm: 44, md: 48 },
                  boxShadow: "0 4px 12px rgba(251, 194, 235, 0.4)",
                  transition: "all 0.3s ease",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": {
                    background: "linear-gradient(135deg, #ff9a9e 0%, #fbc2eb 100%)",
                    transform: "scale(1.1)",
                    boxShadow: "0 6px 20px rgba(251, 194, 235, 0.6)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                size={buttonSize}
              >
                <KitchenIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              </IconButton>

              <Button
                variant="contained"
                size={buttonSize}
                sx={{
                  background: printingEnabled
                    ? "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)"
                    : "rgba(200, 200, 200, 0.5)",
                  color: "#fff",
                  borderRadius: { xs: 2, sm: 2.5, md: 3 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  py: { xs: 0.75, sm: 1, md: 1.25 },
                  fontWeight: 700,
                  fontSize: fontSize.button,
                  boxShadow: printingEnabled
                    ? "0 4px 12px rgba(76, 175, 80, 0.4)"
                    : "none",
                  transition: "all 0.3s ease",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": printingEnabled
                    ? {
                        background: "linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(76, 175, 80, 0.6)",
                      }
                    : {},
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  "&:disabled": {
                    background: "rgba(200, 200, 200, 0.3)",
                    color: "rgba(255, 255, 255, 0.4)",
                  },
                }}
                disabled={!printingEnabled}
                onClick={handlePrintPreparation}
                title="طباعة تجهيز (F9)"
              >
                {printingEnabled ? "طباعة تجهيز" : "لا يوجد"}
              </Button>

              <Menu
                anchorEl={kitchenMenuAnchor}
                open={Boolean(kitchenMenuAnchor)}
                onClose={() => setKitchenMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                    border: "1px solid rgba(251, 194, 235, 0.2)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                    maxWidth: { xs: "90vw", sm: "400px" },
                  },
                }}
              >
                {pendingItems.length > 0 ? (
                  pendingItems.map((item) => (
                    <MenuItem
                      key={item.id}
                      sx={{
                        color: "#fff",
                        fontSize: { xs: 13, sm: 14, md: 15 },
                        py: { xs: 1, sm: 1.25 },
                        px: { xs: 2, sm: 2.5 },
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "rgba(251, 194, 235, 0.1)",
                        },
                      }}
                    >
                      {item.product_name} - الكمية: {item.quantity}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem
                    sx={{
                      color: "rgba(255, 255, 255, 0.6)",
                      fontSize: { xs: 13, sm: 14, md: 15 },
                      py: { xs: 1, sm: 1.25 },
                      px: { xs: 2, sm: 2.5 },
                      justifyContent: "center",
                    }}
                  >
                    لا توجد أصناف قيد التجهيز
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>

        {/* Items & Invoice (right on wide screens, bottom on narrow) */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: spacing,
            overflow: "hidden",
          }}
        >
          <OrderItemsList
            items={orderDetails.items.map((item) => ({
              ...item,
              status: item.status || "pending",
            }))}
            onDeleteItem={handleDeleteItem}
            onEditItem={(item) => {
              setSelectedItem(item);
              setItemEditOpen(true);
            }}
            onMoveItem={(item) => {
              setSelectedMoveItem(item);
              setMoveModalOpen(true);
            }}
            tables={tables}
            preparationCasherEnabled={preparationCasherEnabled}
            onPrepareItem={handlePrepareItem}
            changeOrderItemStatus={changeOrderItemStatus}
          />

          <MoveItemModal
            open={moveModalOpen}
            onClose={() => setMoveModalOpen(false)}
            onSubmit={handleMoveItem}
            item={selectedMoveItem}
            tables={tables}
          />

          <EditOrderForm
            open={editOpen}
            onClose={(updatedData) => {
              setEditOpen(false);
              if (updatedData) {
                updateItem(updatedData);
                window.dispatchEvent(
                  new CustomEvent("orderUpdated", {
                    detail: { orderId, tableId: orderDetails.table_id },
                  })
                );
              }
            }}
            initialData={editData || {}}
          />

          <EditItemModal
            open={itemEditOpen}
            item={selectedItem}
            onClose={() => setItemEditOpen(false)}
          />
        </Box>
      </Box>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiSnackbar-root": {
            maxWidth: { xs: "90vw", sm: "500px" },
          },
        }}
      >
        <Alert
          severity="success"
          sx={{
            width: "100%",
            fontSize: { xs: 13, sm: 14, md: 15 },
            borderRadius: { xs: 2, sm: 2.5, md: 3 },
            boxShadow: "0 4px 16px rgba(76, 175, 80, 0.3)",
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetailsFetcher;
